import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Context from './context';
import api from './api';
import { Context as UserPreferencesContext } from '../userPreferences/context';
import {
    createAppTheme,
    palette as lightPalette,
    darkPalette,
    applyBrandColors
} from '../../components/App/defaultTheme';
import { Context as DesignTokensContext } from '../designTokens';
import { DEFAULT_TOKENS } from '../../styles/designTokens';
import { getThemePreset, themePresetToCss } from '../../styles/themePresets';

const THEME_MODE_KEY = 'taytan-theme-mode';
const THEME_PRESET_KEY = 'taytan-theme-preset';
const PRESET_STYLE_ID = 'taytan-theme-preset';

function getSavedMode() {
    try {
        return localStorage.getItem(THEME_MODE_KEY) || 'light';
    } catch {
        return 'light';
    }
}

function getSavedPreset() {
    try {
        return localStorage.getItem(THEME_PRESET_KEY) || 'default';
    } catch {
        return 'default';
    }
}

const ThemesProvider = ({ defaultTheme = {}, children }) => {
    // localStorage is the pre-login / fast cache (avoids a light→dark flash on
    // first paint before the user profile has loaded).
    const [mode, setModeState] = useState(getSavedMode);
    const [theme, setThemeLocal] = useState(createTheme(defaultTheme));

    // Account-level preference — the source of truth once the profile loads.
    const { preferences, loaded: preferencesLoaded, update: updatePreferences } = useContext(UserPreferencesContext);
    const profileMode = preferences?.themeMode;

    // The user's colour theme (avatar menu). Cached in localStorage like the
    // mode, so a cold start paints it before the profile has loaded.
    const [presetId, setPresetId] = useState(getSavedPreset);
    const preset = getThemePreset(presetId);

    // The tenant's brand colours. This provider sits inside DesignTokensProvider,
    // so the tokens are already resolved by the time the theme is built.
    const { tokens } = useContext(DesignTokensContext);
    const tenantBrand = tokens?.color;

    // A preset applies in light mode only, over the tenant's colours.
    const presetActive = mode === 'light' && preset.id !== 'default';
    const brand = useMemo(
        () => (presetActive ? { ...DEFAULT_TOKENS.color, ...tenantBrand, ...preset.color } : tenantBrand),
        [presetActive, preset, tenantBrand]
    );
    const surface = presetActive ? preset.surface : undefined;

    // Rebuild theme when the mode or the brand colours change + set data-theme
    // on body for CSS overrides
    useEffect(() => {
        let pal = mode === 'dark' ? darkPalette : lightPalette;
        if (surface) pal = { ...pal, background: { ...pal.background, default: surface.page, paper: surface.card } };
        setThemeLocal(createTheme(createAppTheme(applyBrandColors(pal, brand))));
        document.body.setAttribute('data-theme', mode);
    }, [mode, surface, brand?.primary, brand?.accent, brand?.secondary, brand?.header, brand?.footer, brand?.footerText]); // eslint-disable-line react-hooks/exhaustive-deps

    // The preset's CSS variables, in their own sink — never inline on <html>
    // (see designTokens/provider.tsx). Empty when no preset is active.
    useEffect(() => {
        let styleEl = document.getElementById(PRESET_STYLE_ID);
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = PRESET_STYLE_ID;
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = presetActive ? themePresetToCss(preset, brand) : '';
    }, [presetActive, preset, brand]);

    // Once the profile loads (or changes on another device), it is the source
    // of truth for the preset; absent there means the company default.
    useEffect(() => {
        if (!preferencesLoaded) return;
        const profilePreset = preferences?.themePreset || 'default';
        if (profilePreset === presetId) return;
        try { localStorage.setItem(THEME_PRESET_KEY, profilePreset); } catch { /* storage blocked — the profile still applies */ }
        setPresetId(profilePreset);
    }, [preferencesLoaded, preferences?.themePreset]); // eslint-disable-line react-hooks/exhaustive-deps

    // When the profile loads (or is changed on another device), adopt its
    // saved theme mode and mirror it into localStorage for the next cold start.
    useEffect(() => {
        if (!profileMode || profileMode === mode) return;
        try { localStorage.setItem(THEME_MODE_KEY, profileMode); } catch {}
        setModeState(profileMode);
    }, [profileMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const updatePreferencesRef = useRef(updatePreferences);
    updatePreferencesRef.current = updatePreferences;

    const toggleThemeMode = useCallback(() => {
        setModeState((prev) => {
            const next = prev === 'light' ? 'dark' : 'light';
            try { localStorage.setItem(THEME_MODE_KEY, next); } catch {}
            // Persist to the user profile so the choice follows the account
            // across devices/browsers. Fire-and-forget — localStorage already
            // covers the local session if the request fails.
            try { updatePreferencesRef.current?.({ themeMode: next }); } catch {}
            return next;
        });
    }, []);

    const setThemePreset = useCallback((id) => {
        setPresetId(id);
        try { localStorage.setItem(THEME_PRESET_KEY, id); } catch { /* storage blocked — the profile still saves it */ }
        // Fire-and-forget, as for the mode above.
        updatePreferencesRef.current?.({ themePreset: id })?.catch?.(() => undefined);
    }, []);

    const overrideTheme = useCallback(
        (obj) => {
            setThemeLocal(
                createTheme({
                    ...theme,
                    ...obj
                })
            );
        },
        [theme]
    );
    const setTheme = useCallback((obj) => {
        setThemeLocal(createTheme(obj));
    }, []);

    const put = useCallback((body) => api.put(body).then((a) => setTheme(createTheme(a))), [setTheme]);

    const contextValue = useMemo(
        () => ({ theme, overrideTheme, setTheme, put, mode, toggleThemeMode, themePreset: preset.id, setThemePreset }),
        [theme, overrideTheme, setTheme, put, mode, toggleThemeMode, preset.id, setThemePreset]
    );

    return (
        <Context.Provider value={contextValue}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    {children}
                </ThemeProvider>
            </StyledEngineProvider>
        </Context.Provider>
    );
};

ThemesProvider.propTypes = {
    children: PropTypes.node.isRequired,
    defaultTheme: PropTypes.shape({})
};

export default ThemesProvider;
